import { ROUTE_PAYMENT, ROUTE_SIGN_IN } from '@/constants/routes';
import authService from '@/services/auth.service';
import envService from '@/services/env.service';
import sentryService from '@/services/sentry.service';
import { getRoute } from '@/utils';
import axios, { isAxiosError } from 'axios';
import qs from 'qs';
import apiClient from './api';
import { attachAuthToken, shouldBypassRedirectForUrl, shouldRedirectOn401 } from './auth-interceptor';

/**
 * Custom fetcher function for Orval-generated API client
 * Uses the apiClient with its interceptors for authentication
 */
export const customFetcher = <T>({
  url,
  method,
  params,
  data,
  headers,
  signal,
}: {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<T> => {
  return apiClient({
    url,
    method,
    params,
    data,
    headers,
    signal,
  }).then((res) => res.data as T);
};

export const catchBackendApiClient = axios.create({
  baseURL: envService.getEnv().VITE_CATCH_BACKEND_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Use custom params serializer to handle arrays without brackets
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: 'repeat' });
  },
});

catchBackendApiClient.interceptors.request.use(attachAuthToken);

/**
 * Add a response interceptor to handle auth/membership errors consistently
 * with the legacy apiClient interceptor in api.ts.
 */
catchBackendApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!isAxiosError(error)) {
      return Promise.reject(error);
    }

    // Capture API errors in Sentry with structured context. Sentry's global
    // handlers would catch unhandled rejections eventually, but without the
    // URL/method/status tags that make the legacy apiClient errors easy to
    // filter — match that behavior here.
    if (error.response) {
      sentryService.captureException(error, {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response.status,
        statusText: error.response.statusText,
        responseData: error.response.data,
      });
    }

    // window.location.href triggers a full page reload that terminates any
    // in-flight JS — so we must await authService.logout() (which invalidates
    // the WorkOS server-side cookie) BEFORE reloading. Otherwise the next page
    // boot re-hydrates the WorkOS session from a still-live cookie. After the
    // location change is queued we still propagate the rejection so callers
    // (React Query, components) see the original error.
    if (error?.response?.status === 401) {
      // Funnel-session endpoints sit pre-auth: a 401 there means the
      // anonymous token expired/was revoked, not that the user needs to
      // sign in. Let the caller catch the rejection and mint a new session.
      if (shouldBypassRedirectForUrl(error.config?.url)) {
        return Promise.reject(error);
      }
      if (shouldRedirectOn401()) {
        return authService.logout().finally(() => {
          window.location.href = getRoute(ROUTE_SIGN_IN);
        }).then(() => Promise.reject(error), () => Promise.reject(error));
      }
      return Promise.reject(error);
    } else if (error?.response?.status === 402 || error?.response?.status === 403) {
      return authService.logout().finally(() => {
        window.location.href = getRoute(ROUTE_PAYMENT);
      }).then(() => Promise.reject(error), () => Promise.reject(error));
    }

    return Promise.reject(error);
  }
);

export const customCatchBackendFetcher = <T>({
  url,
  method,
  params,
  data,
  headers,
  signal,
}: {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: Record<string, any>;
  data?: any;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}): Promise<T> => {
  return catchBackendApiClient({
    url,
    method,
    params,
    data,
    headers,
    signal,
  }).then((res) => res.data as T);
};
